import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Box, Stack, TextField, Switch, FormControlLabel, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OfferListItem, OfferPromotion, OfferPromotionDTO, OfferPromotionLocalization } from '@modules/offers/types';
import { postOfferPromotion } from '@modules/offers/services/api';
import { handleSuccessNotification, handleErrorNotification } from '@core/utils/notifications';
import { JsonEditor } from '@shared/components/JsonEditor/JsonEditor';
import { DrawerContent, DrawerFooter, DrawerHeader } from '@shared/components/drawer';
import { OfferBasicInfo } from '../../shared/components/Cards/OfferBasicInfo';

interface PromotionDrawerProps {
  open: boolean;
  onClose: () => void;
  offer?: Partial<OfferListItem> | null;
  offerId?: number;
  mode?: 'listing' | 'inline';
  initialPromotion?: OfferPromotionDTO | OfferPromotion | null;
  onInlineSave?: (promotion: OfferPromotionDTO) => void;
}

export const PromotionDrawer: React.FC<PromotionDrawerProps> = ({
  open,
  onClose,
  offer,
  offerId,
  mode = 'listing',
  initialPromotion,
  onInlineSave,
}) => {
  const queryClient = useQueryClient();

  const [popupTitle, setPopupTitle] = useState<OfferPromotionLocalization>(
    initialPromotion?.popup_title || { en: '', de: '' },
  );
  const [description, setDescription] = useState<OfferPromotionLocalization>(
    initialPromotion?.description || { en: '', de: '' },
  );
  const [buttonText, setButtonText] = useState<OfferPromotionLocalization>(
    initialPromotion?.button_text || { en: '', de: '' },
  );

  // Create stable callbacks for JsonEditor to prevent editor recreation
  const handlePopupTitleChange = useCallback((val: any) => {
    setPopupTitle(val);
  }, []);

  const handleDescriptionChange = useCallback((val: any) => {
    setDescription(val);
  }, []);

  const handleButtonTextChange = useCallback((val: any) => {
    setButtonText(val);
  }, []);

  const handleJsonError = useCallback(() => {
    // No-op for now, could show validation error if needed
  }, []);

  const handleJsonClearError = useCallback(() => {
    // No-op for now
  }, []);

  const [form, setForm] = useState({
    offer_promotion_id: initialPromotion?.offer_promotion_id,
    frequency: initialPromotion?.frequency ?? 0,
    max_show_limit: initialPromotion?.max_show_limit ?? 1,
    priority: initialPromotion?.priority ?? 1,
    show_after_offer_purchased: initialPromotion?.show_after_offer_purchased ?? false,
    show_after_last_popup_seconds: initialPromotion?.show_after_last_popup_seconds ?? 0,
  });

  // Reset form when initial values change
  useEffect(() => {
    setForm({
      offer_promotion_id: initialPromotion?.offer_promotion_id,
      frequency: initialPromotion?.frequency ?? 0,
      max_show_limit: initialPromotion?.max_show_limit ?? 1,
      priority: initialPromotion?.priority ?? 1,
      show_after_offer_purchased: initialPromotion?.show_after_offer_purchased ?? false,
      show_after_last_popup_seconds: initialPromotion?.show_after_last_popup_seconds ?? 0,
    });
    setPopupTitle(initialPromotion?.popup_title || { en: '', de: '' });
    setDescription(initialPromotion?.description || { en: '', de: '' });
    setButtonText(initialPromotion?.button_text || { en: '', de: '' });
  }, [initialPromotion]);

  // Optimized change handler for numeric fields
  const handleNumericChange = useCallback((field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const mutation = useMutation({
    mutationFn: (payload: OfferPromotionDTO) => postOfferPromotion(payload),
    onSuccess: (data) => {
      handleSuccessNotification(data, 'Promotion saved successfully');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      onClose();
    },
    onError: (error) => {
      handleErrorNotification(error, 'Failed to save promotion');
    },
  });

  const handleSave = async () => {
    const payload: OfferPromotionDTO = {
      offer_promotion_id: form.offer_promotion_id,
      offer_id: Number(offer?.offer_id ?? offerId ?? 0),
      popup_title: popupTitle,
      description: description,
      button_text: buttonText,
      frequency: Number(form.frequency),
      max_show_limit: Number(form.max_show_limit),
      priority: Number(form.priority),
      show_after_offer_purchased: !!form.show_after_offer_purchased,
      show_after_last_popup_seconds: Number(form.show_after_last_popup_seconds),
    };
    if (mode === 'inline') {
      onInlineSave?.(payload);
      onClose();
    } else {
      await mutation.mutateAsync(payload);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', md: 480 } } } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <DrawerHeader title="Configure Promotion" onClose={onClose} />

        <DrawerContent>
          <Stack spacing={4}>
            {offer && <OfferBasicInfo offer={offer} />}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Popup Title
              </Typography>
              <JsonEditor
                name="popup_title"
                value={popupTitle}
                onChange={handlePopupTitleChange}
                setError={handleJsonError}
                clearErrors={handleJsonClearError}
                data-testid="promotion-popup-title-editor"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Description
              </Typography>
              <JsonEditor
                name="description"
                value={description}
                onChange={handleDescriptionChange}
                setError={handleJsonError}
                clearErrors={handleJsonClearError}
                data-testid="promotion-description-editor"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Button Text
              </Typography>
              <JsonEditor
                name="button_text"
                value={buttonText}
                onChange={handleButtonTextChange}
                setError={handleJsonError}
                clearErrors={handleJsonClearError}
                data-testid="promotion-button-text-editor"
              />
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                type="number"
                label="Frequency"
                value={form.frequency}
                onChange={(e) => handleNumericChange('frequency', e.target.value)}
              />
              <TextField
                type="number"
                label="Max Show Limit"
                value={form.max_show_limit}
                onChange={(e) => handleNumericChange('max_show_limit', e.target.value)}
              />
              <TextField
                type="number"
                label="Priority"
                value={form.priority}
                onChange={(e) => handleNumericChange('priority', e.target.value)}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={form.show_after_offer_purchased}
                  onChange={(e) => setForm((p) => ({ ...p, show_after_offer_purchased: e.target.checked }))}
                />
              }
              label="Show After Offer Purchased"
            />

            <TextField
              type="number"
              label="Show After Last Popup (seconds)"
              value={form.show_after_last_popup_seconds}
              onChange={(e) => handleNumericChange('show_after_last_popup_seconds', e.target.value)}
            />
          </Stack>
        </DrawerContent>

        <DrawerFooter
          primaryButton={{
            label: 'Save Promotion',
            onClick: handleSave,
            loading: mutation.isPending,
          }}
        />
      </Box>
    </Drawer>
  );
};
